import { useCallback, useEffect, useState } from "react";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useTheme } from "@/contexts/ThemeContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Palette, Check, ChevronDown, Sun, Moon, Monitor } from "lucide-react";
import { cn } from "@/lib/utils";

export type ThemeVariant = "clean" | "ocean" | "butler" | "barlis";

const THEME_VARIANTS: readonly ThemeVariant[] = ["clean", "ocean", "butler", "barlis"];
const THEME_VARIANT_KEY = "theme-variant";
const DEFAULT_THEME_VARIANT: ThemeVariant = "butler";

// The variant names are brands, not copy, so they stay as they are in every locale.
const THEME_LABELS: Record<ThemeVariant, string> = {
  clean: "Clean",
  ocean: "Ocean",
  barlis: "Bärlis",
  butler: "Butler",
};

function isThemeVariant(value: string | null): value is ThemeVariant {
  return value !== null && (THEME_VARIANTS as readonly string[]).includes(value);
}

function readThemeVariant(): ThemeVariant {
  if (typeof window === "undefined") return DEFAULT_THEME_VARIANT;
  try {
    const saved = localStorage.getItem(THEME_VARIANT_KEY);
    return isThemeVariant(saved) ? saved : DEFAULT_THEME_VARIANT;
  } catch {
    return DEFAULT_THEME_VARIANT;
  }
}

/**
 * The colour theme (independent of light/dark mode). Persisted per browser and
 * applied as `data-theme` on the document; `public/theme-init.js` reads the
 * same key before first paint.
 */
export function useThemeVariant(): [ThemeVariant, (next: ThemeVariant) => void] {
  const [variant, setVariant] = useState<ThemeVariant>(readThemeVariant);

  useEffect(() => {
    try {
      localStorage.setItem(THEME_VARIANT_KEY, variant);
    } catch {
      // Private mode or blocked storage: the choice lasts for this page only.
    }
    if (variant === "clean") {
      document.documentElement.removeAttribute("data-theme");
    } else {
      document.documentElement.setAttribute("data-theme", variant);
    }
  }, [variant]);

  const set = useCallback((next: ThemeVariant) => setVariant(next), []);
  return [variant, set];
}

type Mode = "light" | "dark" | "system";

const MODE_ICONS: Record<Mode, typeof Sun> = { light: Sun, dark: Moon, system: Monitor };

function useModeLabels(): Record<Mode, string> {
  const { i18n } = useLingui();
  return {
    light: i18n._({ id: "appearance.mode.light", message: "Light" }),
    dark: i18n._({ id: "appearance.mode.dark", message: "Dark" }),
    system: i18n._({ id: "appearance.mode.system", message: "System" }),
  };
}

/** The mode and theme choices as dropdown-menu items, for the user menu. */
export function AppearanceMenuItems() {
  const { mode, setMode } = useTheme();
  const [variant, setVariant] = useThemeVariant();
  const modeLabels = useModeLabels();

  return (
    <>
      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
        <Trans id="appearance.mode.label">Mode</Trans>
      </DropdownMenuLabel>
      <DropdownMenuRadioGroup value={mode} onValueChange={(value) => setMode(value as Mode)}>
        {(["light", "dark", "system"] as const).map((value) => {
          const Icon = MODE_ICONS[value];
          return (
            <DropdownMenuRadioItem key={value} value={value}>
              <Icon className="mr-2 h-4 w-4" aria-hidden="true" />
              {modeLabels[value]}
            </DropdownMenuRadioItem>
          );
        })}
      </DropdownMenuRadioGroup>
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
        <Trans id="appearance.theme.label">Theme</Trans>
      </DropdownMenuLabel>
      <DropdownMenuRadioGroup value={variant} onValueChange={(value) => isThemeVariant(value) && setVariant(value)}>
        {THEME_VARIANTS.map((value) => (
          <DropdownMenuRadioItem key={value} value={value}>
            {THEME_LABELS[value]}
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </>
  );
}

interface AppearanceMenuProps {
  className?: string;
}

/** A standalone popover (used outside the sidebar, e.g. on auth pages). */
export function AppearanceMenu({ className }: AppearanceMenuProps) {
  const { mode, setMode } = useTheme();
  const [variant, setVariant] = useThemeVariant();
  const modeLabels = useModeLabels();
  const [open, setOpen] = useState(false);
  const { i18n } = useLingui();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn("justify-between", className)}>
          <span className="flex items-center gap-2">
            <Palette className="h-4 w-4" aria-hidden="true" />
            {i18n._({ id: "appearance.title", message: "Appearance" })}
          </span>
          <ChevronDown className="ml-2 h-4 w-4" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-48 p-1">
        <Label className="mb-1 block px-2 text-xs text-muted-foreground">
          <Trans id="appearance.mode.label">Mode</Trans>
        </Label>
        {(["light", "dark", "system"] as const).map((value) => {
          const Icon = MODE_ICONS[value];
          return (
            <Button
              key={value}
              variant="ghost"
              onClick={() => setMode(value)}
              className="h-auto w-full justify-between p-2 hover:bg-accent"
            >
              <span className="flex items-center gap-2">
                <Icon className="h-4 w-4" aria-hidden="true" />
                {modeLabels[value]}
              </span>
              {mode === value && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
            </Button>
          );
        })}
        <Separator className="my-1" />
        <Label className="mb-1 block px-2 text-xs text-muted-foreground">
          <Trans id="appearance.theme.label">Theme</Trans>
        </Label>
        {THEME_VARIANTS.map((value) => (
          <Button
            key={value}
            variant="ghost"
            onClick={() => setVariant(value)}
            className="h-auto w-full justify-between p-2 hover:bg-accent"
          >
            <span>{THEME_LABELS[value]}</span>
            {variant === value && <Check className="h-4 w-4 text-primary" aria-hidden="true" />}
          </Button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
